import React, { useState } from "react";
import { DEFAULT_NODES, DEFAULT_EDGES, CATEGORIES, EDGE_TYPES } from "./relationMap.config";
import RelationMapModal from "./RelationMapModal";

// ─── Mini SVG thumbnail ───────────────────────────────────────────────────────

function MiniMap({ nodes, edges, w = 40, h = 28 }) {
    const xs = nodes.map(n => n.x);
    const ys = nodes.map(n => n.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const rangeX = (Math.max(...xs) - minX) || 1;
    const rangeY = (Math.max(...ys) - minY) || 1;
    const pad = 3;

    const sx = (v) => pad + ((v - minX) / rangeX) * (w - 2 * pad);
    const sy = (v) => pad + ((v - minY) / rangeY) * (h - 2 * pad);

    const positioned = nodes.map(n => ({ ...n, px: sx(n.x), py: sy(n.y) }));
    const byId = {};
    positioned.forEach(n => byId[n.id] = n);

    return (
        <svg width={w} height={h} style={{ overflow: "visible", flexShrink: 0 }}>
            {edges.map(e => {
                const f = byId[e.from];
                const t = byId[e.to];
                if (!f || !t) return null;
                return (
                    <line
                        key={e.id}
                        x1={f.px} y1={f.py} x2={t.px} y2={t.py}
                        stroke={EDGE_TYPES[e.type]?.color || "#94a3b8"}
                        strokeWidth={0.6}
                        opacity={0.55}
                    />
                );
            })}
            {positioned.map(n => (
                <circle
                    key={n.id}
                    cx={n.px} cy={n.py} r={2.2}
                    fill={CATEGORIES[n.category]?.color || "#94a3b8"}
                />
            ))}
        </svg>
    );
}

// ─── RelationMapButton ────────────────────────────────────────────────────────

export default function RelationMapButton({
    size = "default",
    nodes = DEFAULT_NODES,
    edges = DEFAULT_EDGES,
    label = "Relation Map",
}) {
    const [open, setOpen] = useState(false);

    const modal = open ? (
        <RelationMapModal
            onClose={() => setOpen(false)}
            initialNodes={nodes}
            initialEdges={edges}
        />
    ) : null;

    if (size === "icon") {
        return (
            <>
                <button
                    onClick={() => setOpen(true)}
                    title="Platform Relation Map"
                    className="w-10 h-10 bg-navy-900 hover:bg-navy-800 rounded-xl flex items-center justify-center transition-all shadow-lg shadow-navy-900/20"
                >
                    <MiniMap nodes={nodes} edges={edges} w={30} h={20} />
                </button>
                {modal}
            </>
        );
    }

    if (size === "small") {
        return (
            <>
                <button
                    onClick={() => setOpen(true)}
                    className="flex items-center gap-2 px-3 py-2 bg-navy-900 hover:bg-navy-800 text-white rounded-xl transition-all text-xs font-bold shadow-md shadow-navy-900/20"
                >
                    <MiniMap nodes={nodes} edges={edges} w={22} h={15} />
                    Map
                </button>
                {modal}
            </>
        );
    }

    // default — full size
    return (
        <>
            <button
                onClick={() => setOpen(true)}
                className="flex items-center gap-3 px-5 py-2.5 bg-navy-900 hover:bg-navy-800 text-white rounded-xl font-bold transition-all shadow-lg shadow-navy-900/20"
            >
                <MiniMap nodes={nodes} edges={edges} w={38} h={26} />
                {label}
            </button>
            {modal}
        </>
    );
}
